import { ChangeDetectionStrategy, Component, computed, inject, OnInit, DestroyRef, signal } from '@angular/core';
import { Router, RouterLink, RouterOutlet } from '@angular/router';
import { AuthService } from './auth/services/auth.service';
import { Title } from '@angular/platform-browser';
import { TranslateService } from '@ngx-translate/core';
import { tap } from 'rxjs';
import { Store } from '@ngxs/store';
import { toSignal } from '@angular/core/rxjs-interop';
import {
    SiApplicationHeaderComponent,
    SiHeaderActionsDirective,
    SiHeaderBrandDirective,
    SiHeaderLogoDirective
} from '@siemens/element-ng/application-header';
import { SiNavbarVerticalComponent } from '@siemens/element-ng/navbar-vertical';
import type { NavbarVerticalItem } from '@siemens/element-ng/navbar-vertical';
import { SiFooterComponent } from '@siemens/element-ng/footer';
import { UserComponent } from './shared/components/user/user.component';
import { ThemeToggleComponent } from './shared/components/theme-toggle/theme-toggle.component';
import { LanguageSwitcherComponent } from './shared/components/language-switcher/language-switcher.component';
import { SupportLinkComponent } from './shared/components/support-link/support-link.component';
import { ThemeService } from './shared/services/theme.service';
import { footerCopyright, footerItems } from '../configs/footer-items.config';
import { SiLoadingSpinnerComponent } from '@siemens/element-ng/loading-spinner';
import { navbarVerticalItems, adminNavbarItems } from '../configs/navbar-vertical-items.config';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserState } from './auth/state/user.state';
import { NavbarService } from './core/services';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        RouterOutlet,
        SiHeaderActionsDirective,
        SiHeaderBrandDirective,
        SiNavbarVerticalComponent,
        SiApplicationHeaderComponent,
        RouterLink,
        SiHeaderLogoDirective,
        UserComponent,
        ThemeToggleComponent,
        LanguageSwitcherComponent,
        SupportLinkComponent,
        SiFooterComponent,
        SiLoadingSpinnerComponent
    ]
})
export class AppComponent implements OnInit {
    protected readonly authService = inject(AuthService);
    private readonly router = inject(Router);
    private readonly translateService = inject(TranslateService);
    private readonly pageTitleService = inject(Title);
    private readonly themeService = inject(ThemeService);
    private readonly destroyRef = inject(DestroyRef);

    private readonly store = inject(Store);
    private readonly isAdmin = toSignal(this.store.select(UserState.isAdmin), { initialValue: false });
    private readonly navbarService = inject(NavbarService);
    /** Incremented on every language change so navbarItems re-computes with translated badge text. */
    private readonly langVersion = signal(0);

    protected readonly navbarItems = computed((): NavbarVerticalItem[] => {
        // Child routes can override the navbar items (e.g. project detail sub-nav).
        const override = this.navbarService.items();
        if (override !== null) {
            return override;
        }
        // Default: depend on langVersion so this recomputes when the language changes.
        this.langVersion();
        const badgeSoon = this.translateService.instant('sidebar.badge-soon') as string;
        if (!this.isAdmin()) {
            return navbarVerticalItems;
        }
        // Clone admin items with the translated badge value.
        const translatedAdmin = adminNavbarItems.map(item => ('badge' in item ? { ...item, badge: badgeSoon } : item));
        return [...navbarVerticalItems, ...translatedAdmin];
    });

    protected readonly footerCopyright = footerCopyright;
    protected readonly footerItems = footerItems;

    ngOnInit(): void {
        this.themeService.initialize();
        this.subscribeToLanguageChanges();
        this.updatePageTitleTranslation();
    }

    protected isRouterPathUnauthorized(): boolean {
        return this.router.url === '/unauthorized';
    }

    protected isRouterPathNotFound(): boolean {
        return this.router.url === '/not-found';
    }

    private updatePageTitleTranslation(): void {
        // Re-translate the current route's page title rather than always using the home title.
        // Each routed component sets its own title via pageTitleService.setTitle() on init,
        // so on language change we trigger a re-navigation to the same URL to re-run the component init.
        // As a lightweight alternative, we only reset to home when no component-specific title is active.
        const currentTitle = this.pageTitleService.getTitle();
        const homeTitle = this.translateService.instant('page-titles.home');
        // Only reset to home if we're actually on the home/root route
        if (this.router.url === '/' || this.router.url === '') {
            this.pageTitleService.setTitle(homeTitle);
        } else if (currentTitle === this.translateService.instant('page-titles.home')) {
            // Title is still home but we're on another route — set to home (edge case on first load)
            this.pageTitleService.setTitle(homeTitle);
        }
    }

    private subscribeToLanguageChanges(): void {
        this.translateService.onLangChange
            .pipe(
                tap(() => {
                    this.updatePageTitleTranslation();
                    this.langVersion.update(v => v + 1);
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }
}
